import { useEffect, useState } from 'react';

import {
  usePaginatedStudentsLazyQuery,
  usePaginatedGroupsLazyQuery,
  usePaginatedCoursesLazyQuery,
  useAllTeachersLazyQuery,
  usePaginatedUsersSettingsLazyQuery,
  usePaletteLeadsLazyQuery,
  PaginatedStudentsQuery,
  PaginatedGroupsQuery,
  PaginatedCoursesQuery,
  AllTeachersQuery,
  PaginatedUsersSettingsQuery,
  PaletteLeadsQuery,
} from 'core/graphql';
import { IGroup, PublishedLmsType, UserRole } from 'types';
import {
  IPaletteStudentEntity,
  IPaletteGroupEntity,
  IPaletteCourseEntity,
  IPaletteTeacherEntity,
  IPaletteUserEntity,
  IPaletteLeadEntity,
} from 'core/state';

import {
  visibleFor,
  STUDENT_ROLES,
  EDUCATION_ROLES,
  ADMIN_ROLES,
  TEACHER_PROFILE_ROLES,
} from './paletteRoles';
import { LEAD_ROLES } from '../Crm/useLeadPermissions';

export const SEARCH_DEBOUNCE_MS = 300;
export const SEARCH_MIN_CHARS = 2;
const SEARCH_LIMIT = 5;

export interface IPaletteSearchResults {
  students: IPaletteStudentEntity[];
  groups: IPaletteGroupEntity[];
  courses: IPaletteCourseEntity[];
  teachers: IPaletteTeacherEntity[];
  users: IPaletteUserEntity[];
  leads: IPaletteLeadEntity[];
}

export interface IUsePaletteSearch {
  results: IPaletteSearchResults;
  loading: boolean;
  hasQuery: boolean;
}

const EMPTY_RESULTS: IPaletteSearchResults = {
  students: [],
  groups: [],
  courses: [],
  teachers: [],
  users: [],
  leads: [],
};

const fullNameOf = (
  user?: {
    lastName?: string | null;
    firstName?: string | null;
    middleName?: string | null;
  } | null,
): string =>
  [user?.lastName, user?.firstName, user?.middleName].filter(Boolean).join(' ');

const joinMeta = (
  parts: Array<string | null | undefined>,
): string | undefined => parts.filter(Boolean).join(' · ') || undefined;

const nodesOf = <TNode>(
  edges: Array<{ node?: TNode | null } | null> | undefined,
): TNode[] =>
  (edges ?? [])
    .map((edge) => edge?.node)
    .filter((node): node is TNode => Boolean(node));

const toStudents = (data?: PaginatedStudentsQuery): IPaletteStudentEntity[] =>
  nodesOf(data?.allStudents?.edges).map((node) => {
    const agreement = (node.agreements ?? [])[0];
    return {
      kind: 'student',
      id: node.id,
      title: fullNameOf(node.user) || '—',
      subtitle: joinMeta([node.user?.phone, node.user?.email]),
      isLmsBlocked: Boolean(node.isLmsBlocked),
      agreement: agreement
        ? {
            id: agreement.id,
            currency: agreement.currency,
            agreementTitle: agreement.agreementTitle,
            group: agreement.group
              ? { id: agreement.group.id, name: agreement.group.name }
              : null,
          }
        : null,
    };
  });

const toGroups = (data?: PaginatedGroupsQuery): IPaletteGroupEntity[] =>
  nodesOf(data?.allGroups?.edges).map((node) => {
    const group: IGroup = {
      id: node.id,
      group: node.name,
      startDate: node.startDate || '',
      publishedLms: Boolean(node.publishedLms),
      publishedLmsForTeacher: Boolean(node.publishedLmsForTeacher),
      publishedLmsForMethodist: Boolean(node.publishedLmsForMethodist),
      publishedLmsType: PublishedLmsType.student,
      numberOfLessons: String(node.lessonsCount ?? 0),
      numberOfStudents: String(node.studentsCount ?? 0),
      lessonDays: [],
      agreementsSum: String(node.contributionsSum ?? ''),
    };
    return {
      kind: 'group',
      id: node.id,
      title: node.name,
      subtitle: node.startDate || undefined,
      publishedLms: Boolean(
        node.publishedLms ||
          node.publishedLmsForTeacher ||
          node.publishedLmsForMethodist,
      ),
      group,
    };
  });

const toCourses = (data?: PaginatedCoursesQuery): IPaletteCourseEntity[] =>
  nodesOf(data?.allCourses?.edges).map((node) => ({
    kind: 'course',
    id: node.id,
    title: node.name,
    subtitle: node.category?.name || node.educationForm || undefined,
  }));

const toTeachers = (data?: AllTeachersQuery): IPaletteTeacherEntity[] =>
  nodesOf(data?.allTeachers?.edges).map((node) => ({
    kind: 'teacher',
    id: node.id,
    title: node.fullName || '—',
    subtitle: node.user?.email || undefined,
  }));

const toUsers = (data?: PaginatedUsersSettingsQuery): IPaletteUserEntity[] =>
  nodesOf(data?.allUsersSettings?.edges).map((node) => ({
    kind: 'user',
    id: node.id,
    title:
      [node.lastName, node.firstName].filter(Boolean).join(' ') ||
      node.username ||
      node.email ||
      '—',
    subtitle: node.email || undefined,
  }));

/**
 * Задача #116: лид — имя сделки, а под ним человек, телефон или email и этап
 * воронки. Имя сделки в Kommo часто техническое («Lead #123»), поэтому
 * человек стоит первым в подзаголовке.
 */
export const toLeads = (data?: PaletteLeadsQuery): IPaletteLeadEntity[] =>
  (data?.kommoLeads?.items ?? []).map((lead) => ({
    kind: 'lead',
    id: lead.id,
    title: lead.name || lead.personName || '—',
    subtitle: joinMeta([
      lead.personName && lead.personName !== lead.name ? lead.personName : null,
      lead.phone || lead.email,
      lead.stage?.name,
    ]),
  }));

/**
 * Resolve one lazy-query result for the current query. Returns:
 *   `null`      — the entity is skipped for this role (settled, no data),
 *   `undefined` — still in flight / tagged with a stale query (pending),
 *   `TData`     — settled data for exactly `query`.
 * The `variables.query` tag is what drops stale responses from a prior keystroke.
 */
const pick = <TData>(
  enabled: boolean,
  res: {
    loading: boolean;
    data?: TData;
    variables?: { query?: string | null };
  },
  query: string,
): TData | null | undefined => {
  if (!enabled) return null;
  if (res.loading) return undefined;
  if ((res.variables?.query ?? '') !== query) return undefined;
  return res.data ?? null;
};

/**
 * Debounced multi-entity search powering the palette. Fires the generated lazy
 * queries in parallel (five lists with `first: 5` plus `PaletteLeads` with its
 * own `limit: 5`, задача #116; all take arg `query`), skipping any entity the
 * operator has no route to. Results are committed atomically only once every
 * enabled query has settled for the current debounced query — so the
 * responses never mix across keystrokes, and the previous results stay on
 * screen while the next set loads (no flicker / blank).
 */
export const usePaletteSearch = (
  query: string,
  roles: UserRole[],
): IUsePaletteSearch => {
  const canStudents = visibleFor(roles, STUDENT_ROLES);
  const canGroups = visibleFor(roles, EDUCATION_ROLES);
  const canCourses = visibleFor(roles, EDUCATION_ROLES);
  // Преподаватели — `TEACHER_PROFILE_ROLES` (тот же список у маршрута и меню).
  const canTeachers = visibleFor(roles, TEACHER_PROFILE_ROLES);
  const canUsers = visibleFor(roles, ADMIN_ROLES);
  // Задача #116: тот же список, что у пункта меню и маршрута «Лиды». Роли без
  // лидов запрос не шлют вовсе — отказ сервера рисовал бы ошибку.
  const canLeads = visibleFor(roles, LEAD_ROLES);

  const [debounced, setDebounced] = useState('');
  const [results, setResults] = useState<{
    query: string;
    data: IPaletteSearchResults;
  }>({ query: '', data: EMPTY_RESULTS });

  const [runStudents, studentsRes] = usePaginatedStudentsLazyQuery({
    fetchPolicy: 'no-cache',
  });
  const [runGroups, groupsRes] = usePaginatedGroupsLazyQuery({
    fetchPolicy: 'no-cache',
  });
  const [runCourses, coursesRes] = usePaginatedCoursesLazyQuery({
    fetchPolicy: 'no-cache',
  });
  const [runTeachers, teachersRes] = useAllTeachersLazyQuery({
    fetchPolicy: 'no-cache',
  });
  const [runUsers, usersRes] = usePaginatedUsersSettingsLazyQuery({
    fetchPolicy: 'no-cache',
  });
  const [runLeads, leadsRes] = usePaletteLeadsLazyQuery({
    fetchPolicy: 'no-cache',
  });

  // Debounce the raw query into `debounced` (>= 2 chars, 300ms idle).
  useEffect(() => {
    const trimmed = query.trim();
    if (trimmed.length < SEARCH_MIN_CHARS) {
      setDebounced('');
      return undefined;
    }
    const timeout = window.setTimeout(
      () => setDebounced(trimmed),
      SEARCH_DEBOUNCE_MS,
    );
    return () => window.clearTimeout(timeout);
  }, [query]);

  // Fire the enabled lazy queries in parallel for the current debounced query.
  useEffect(() => {
    if (!debounced) return;
    const variables = { query: debounced, first: SEARCH_LIMIT };
    if (canStudents) runStudents({ variables });
    // Группы в палитре — без закрытых ролями полей (`price`, `currency`):
    // строке результата они не нужны.
    if (canGroups)
      runGroups({ variables: { ...variables, withStaffFields: false } });
    if (canCourses) runCourses({ variables });
    if (canTeachers) runTeachers({ variables });
    if (canUsers) runUsers({ variables });
    // Своя операция без `first`: размер страницы зашит в документ (`limit`).
    if (canLeads) runLeads({ variables: { query: debounced } });
  }, [
    debounced,
    canStudents,
    canGroups,
    canCourses,
    canTeachers,
    canUsers,
    canLeads,
    runStudents,
    runGroups,
    runCourses,
    runTeachers,
    runUsers,
    runLeads,
  ]);

  // Commit the merged results once EVERY enabled query has settled for the
  // current query — a single atomic swap, guarded so it runs at most once per
  // query (no re-render loop despite the query-result objects changing identity).
  useEffect(() => {
    if (!debounced) {
      if (results.query !== '') {
        setResults({ query: '', data: EMPTY_RESULTS });
      }
      return;
    }
    if (results.query === debounced) return;

    const studentsData = pick(canStudents, studentsRes, debounced);
    const groupsData = pick(canGroups, groupsRes, debounced);
    const coursesData = pick(canCourses, coursesRes, debounced);
    const teachersData = pick(canTeachers, teachersRes, debounced);
    const usersData = pick(canUsers, usersRes, debounced);
    const leadsData = pick(canLeads, leadsRes, debounced);

    const pending = [
      studentsData,
      groupsData,
      coursesData,
      teachersData,
      usersData,
      leadsData,
    ].some((value) => value === undefined);
    if (pending) return;

    setResults({
      query: debounced,
      data: {
        students: toStudents(studentsData ?? undefined),
        groups: toGroups(groupsData ?? undefined),
        courses: toCourses(coursesData ?? undefined),
        teachers: toTeachers(teachersData ?? undefined),
        users: toUsers(usersData ?? undefined),
        leads: toLeads(leadsData ?? undefined),
      },
    });
  }, [
    debounced,
    results,
    canStudents,
    canGroups,
    canCourses,
    canTeachers,
    canUsers,
    canLeads,
    studentsRes,
    groupsRes,
    coursesRes,
    teachersRes,
    usersRes,
    leadsRes,
  ]);

  const hasQuery = query.trim().length >= SEARCH_MIN_CHARS;

  return {
    results: results.data,
    loading: hasQuery && results.query !== debounced,
    hasQuery,
  };
};
